import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock, Loader2, ShieldCheck, XCircle } from "lucide-react";
import platformApi from "@/lib/platformApi";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const GOLD_INK = "var(--brand-accent-ink)";
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB",
  { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function TrialApprovalsPanel({ isRoot, onChanged }) {
  const [apps, setApps] = useState(null);
  const [busy, setBusy] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const load = () => platformApi.get("/trial-approvals")
    .then((r) => setApps(r.data.applications)).catch(() => setApps([]));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const approve = async (sub) => {
    setBusy(sub);
    try {
      const { data } = await platformApi.post(`/trial-approvals/${sub}/approve`);
      toast.success(`Approved ${sub} — trial runs to ${fmt(data.trial_ends_at)}`);
      load(); onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const reject = async () => {
    setRejecting(true);
    try {
      await platformApi.post(`/trial-approvals/${rejectTarget.subdomain}/reject`, { reason });
      toast.success(`Rejected ${rejectTarget.subdomain}`);
      setRejectTarget(null); setReason("");
      load(); onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRejecting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="trial-approvals-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-[13px] font-bold uppercase tracking-[0.14em] text-[#1E293B] flex items-center gap-2">
          <Clock className="h-4 w-4" style={{ color: GOLD_INK }} /> Trial approvals
          <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-slate-100 border-slate-300 text-slate-600">
            {isRoot ? "root approves / rejects" : "view only — root approves"}
          </span>
        </h2>
      </div>

      {!apps ? <Skeleton className="h-40 w-full rounded-none" /> : apps.length === 0 ? (
        <div className="bg-white border border-slate-200 px-5 py-10 text-center" data-testid="approvals-empty">
          <CheckCircle2 className="h-8 w-8 text-teal-600 mx-auto mb-3" />
          <p className="text-[13px] text-slate-500">No applications awaiting approval.</p>
          <p className="text-[11px] text-slate-400 mt-1">Self-serve trial applications appear here for review.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="approvals-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 bg-slate-50">
                {["Brand", "Organization", "Subdomain", "Admin", "Plan", "Applied", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apps.map((a) => (
                <tr key={a.subdomain} data-testid={`approval-row-${a.subdomain}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2" title={a.branding?.preset}>
                      <span className="h-6 w-6 flex items-center justify-center text-[10px] font-bold text-white relative shrink-0"
                        style={{ background: a.branding?.primary_color || "#1E293B" }}>
                        {(a.product_name || a.org_name || "A")[0].toUpperCase()}
                        <span className="absolute bottom-0 left-0 right-0 h-[2px]"
                          style={{ background: a.branding?.accent_color || "#D4A017" }} />
                      </span>
                      <span className="text-[11px] font-semibold text-[#1E293B] truncate max-w-[120px]">{a.product_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-700">{a.org_name}</td>
                  <td className="px-4 py-2.5 font-mono font-semibold text-[#1E293B]">{a.subdomain}</td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-slate-500">{a.admin_email}</td>
                  <td className="px-4 py-2.5 text-slate-500 capitalize">{a.plan}</td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-slate-400">{fmt(a.applied_at)}</td>
                  <td className="px-4 py-2.5">
                    {isRoot ? (
                      <div className="flex items-center gap-1.5">
                        <button data-testid={`approve-btn-${a.subdomain}`} disabled={busy === a.subdomain}
                          onClick={() => approve(a.subdomain)}
                          className="text-[10px] font-mono uppercase border border-teal-300 text-teal-700 px-2 py-1 hover:bg-teal-50 disabled:opacity-50 flex items-center gap-1">
                          {busy === a.subdomain ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} approve
                        </button>
                        <button data-testid={`reject-btn-${a.subdomain}`} disabled={busy === a.subdomain}
                          onClick={() => { setRejectTarget(a); setReason(""); }}
                          className="text-[10px] font-mono uppercase border border-slate-300 text-slate-500 px-2 py-1 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 flex items-center gap-1">
                          <XCircle className="h-3 w-3" /> reject
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] font-mono uppercase text-slate-400 flex items-center gap-1"
                        data-testid={`approval-rootonly-${a.subdomain}`}>
                        <ShieldCheck className="h-3.5 w-3.5" style={{ color: GOLD_INK }} /> root approval required
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <DialogContent className="rounded-none sm:max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="text-[14px] font-bold text-[#1E293B]">
              Reject application — {rejectTarget?.org_name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-slate-500 -mt-1">
            The applicant's login will show a rejection notice. This is recorded in the Observatory feed.
            You can hard-delete the tenant afterwards from the Tenants tab.
          </p>
          <Textarea data-testid="reject-reason-input" value={reason}
            onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="Short reason (e.g. incomplete company details)"
            className="rounded-none text-[13px]" />
          <DialogFooter>
            <Button data-testid="reject-confirm-btn" onClick={reject}
              disabled={rejecting || reason.trim().length < 3}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white">
              {rejecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
              Reject application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
